import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  useDisclosure
} from '@heroui/react';
import { NpcSpelling, LocationSpelling, SpellingsConfig } from 'src/api/types/spellings';
import { MAX_SPELLINGS_PER_TYPE } from 'src/constants/spellings';
import { errorToast } from 'src/utils/toasts';

interface NpcLocationSpellingsEditorProps {
  spellings: SpellingsConfig;
  onUpdate: (spellings: SpellingsConfig) => Promise<void>;
  title: string;
  description?: string;
}

const NpcLocationSpellingsEditor: React.FC<NpcLocationSpellingsEditorProps> = ({
  spellings,
  onUpdate,
  title,
  description
}) => {
  const [npcs, setNpcs] = useState<NpcSpelling[]>(spellings.npcs || []);
  const [locations, setLocations] = useState<LocationSpelling[]>(spellings.locations || []);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);
  const lastSavedStateRef = useRef<{ npcs: NpcSpelling[]; locations: LocationSpelling[] } | null>(
    null
  );
  const isSavingRef = useRef(false);
  const pendingStateRef = useRef<{ npcs: NpcSpelling[]; locations: LocationSpelling[] } | null>(
    null
  );
  const {
    isOpen: isNpcModalOpen,
    onOpen: onNpcModalOpen,
    onClose: onNpcModalClose
  } = useDisclosure();
  const {
    isOpen: isLocationModalOpen,
    onOpen: onLocationModalOpen,
    onClose: onLocationModalClose
  } = useDisclosure();
  const [editingNpc, setEditingNpc] = useState<NpcSpelling | null>(null);
  const [editingLocation, setEditingLocation] = useState<LocationSpelling | null>(null);
  const [originalNpcName, setOriginalNpcName] = useState<string | null>(null);
  const [originalLocationName, setOriginalLocationName] = useState<string | null>(null);
  const [npcName, setNpcName] = useState('');
  const [npcAliases, setNpcAliases] = useState<string[]>([]);
  const [newAlias, setNewAlias] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAliases, setLocationAliases] = useState<string[]>([]);
  const [newLocationAlias, setNewLocationAlias] = useState('');

  useEffect(() => {
    setNpcs(spellings.npcs || []);
    setLocations(spellings.locations || []);
    isInitialMount.current = true;
  }, [spellings]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSavedStateRef.current = { npcs, locations };
      return;
    }

    if (isSavingRef.current) {
      pendingStateRef.current = { npcs, locations };
      return;
    }

    pendingStateRef.current = null;
    const currentState = { npcs, locations };
    const lastSaved = lastSavedStateRef.current;
    if (
      lastSaved &&
      JSON.stringify(lastSaved.npcs) === JSON.stringify(currentState.npcs) &&
      JSON.stringify(lastSaved.locations) === JSON.stringify(currentState.locations)
    ) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await onUpdate({ npcs, locations });
        lastSavedStateRef.current = { npcs, locations };
      } catch (error) {
        console.error('Error saving spellings:', error);
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
        const pending = pendingStateRef.current;
        pendingStateRef.current = null;
        if (
          pending &&
          lastSavedStateRef.current &&
          (JSON.stringify(pending.npcs) !== JSON.stringify(lastSavedStateRef.current.npcs) ||
            JSON.stringify(pending.locations) !==
              JSON.stringify(lastSavedStateRef.current.locations))
        ) {
          saveTimeoutRef.current = setTimeout(async () => {
            isSavingRef.current = true;
            setIsSaving(true);
            try {
              await onUpdate({ npcs: pending.npcs, locations: pending.locations });
              lastSavedStateRef.current = { npcs: pending.npcs, locations: pending.locations };
            } catch (error) {
              console.error('Error saving spellings:', error);
            } finally {
              isSavingRef.current = false;
              setIsSaving(false);
            }
          }, 500);
        }
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [npcs, locations, onUpdate]);

  const handleAddNpc = () => {
    setEditingNpc(null);
    setOriginalNpcName(null);
    setNpcName('');
    setNpcAliases([]);
    setNewAlias('');
    onNpcModalOpen();
  };

  const handleEditNpc = (npc: NpcSpelling) => {
    setEditingNpc(npc);
    setOriginalNpcName(npc.name);
    setNpcName(npc.name);
    setNpcAliases(npc.aliases || []);
    setNewAlias('');
    onNpcModalOpen();
  };

  const handleSaveNpc = () => {
    if (!npcName.trim()) return;

    const trimmedName = npcName.trim();

    if (editingNpc && originalNpcName) {
      const otherNpcs = npcs.filter((n) => n.name !== originalNpcName);
      if (otherNpcs.some((n) => n.name.toLowerCase() === trimmedName.toLowerCase())) {
        errorToast({
          title: 'Duplicate NPC',
          description: 'An NPC with this name already exists.'
        });
        return;
      }
    } else if (!editingNpc) {
      if (npcs.length >= MAX_SPELLINGS_PER_TYPE) {
        errorToast({
          title: 'Limit reached',
          description: `You can only add up to ${MAX_SPELLINGS_PER_TYPE} NPCs.`
        });
        return;
      }
      if (npcs.some((n) => n.name.toLowerCase() === trimmedName.toLowerCase())) {
        errorToast({
          title: 'Duplicate NPC',
          description: 'An NPC with this name already exists.'
        });
        return;
      }
    }

    const newNpc: NpcSpelling = {
      name: trimmedName,
      aliases: npcAliases.length > 0 ? npcAliases : undefined
    };

    if (editingNpc && originalNpcName) {
      setNpcs(npcs.map((n) => (n.name === originalNpcName ? newNpc : n)));
    } else {
      setNpcs([...npcs, newNpc]);
    }

    onNpcModalClose();
  };

  const handleDeleteNpc = (name: string) => {
    setNpcs(npcs.filter((n) => n.name !== name));
  };

  const handleAddAlias = () => {
    if (newAlias.trim() && !npcAliases.includes(newAlias.trim())) {
      setNpcAliases([...npcAliases, newAlias.trim()]);
      setNewAlias('');
    }
  };

  const handleRemoveAlias = (alias: string) => {
    setNpcAliases(npcAliases.filter((a) => a !== alias));
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setOriginalLocationName(null);
    setLocationName('');
    setLocationAliases([]);
    setNewLocationAlias('');
    onLocationModalOpen();
  };

  const handleEditLocation = (location: LocationSpelling) => {
    setEditingLocation(location);
    setOriginalLocationName(location.name);
    setLocationName(location.name);
    setLocationAliases(location.aliases || []);
    setNewLocationAlias('');
    onLocationModalOpen();
  };

  const handleSaveLocation = () => {
    if (!locationName.trim()) return;

    const trimmedName = locationName.trim();

    if (editingLocation && originalLocationName) {
      const otherLocations = locations.filter((l) => l.name !== originalLocationName);
      if (otherLocations.some((l) => l.name.toLowerCase() === trimmedName.toLowerCase())) {
        errorToast({
          title: 'Duplicate Location',
          description: 'A location with this name already exists.'
        });
        return;
      }
    } else if (!editingLocation) {
      if (locations.length >= MAX_SPELLINGS_PER_TYPE) {
        errorToast({
          title: 'Limit reached',
          description: `You can only add up to ${MAX_SPELLINGS_PER_TYPE} locations.`
        });
        return;
      }
      if (locations.some((l) => l.name.toLowerCase() === trimmedName.toLowerCase())) {
        errorToast({
          title: 'Duplicate Location',
          description: 'A location with this name already exists.'
        });
        return;
      }
    }

    const newLocation: LocationSpelling = {
      name: trimmedName,
      aliases: locationAliases.length > 0 ? locationAliases : undefined
    };

    if (editingLocation && originalLocationName) {
      setLocations(locations.map((l) => (l.name === originalLocationName ? newLocation : l)));
    } else {
      setLocations([...locations, newLocation]);
    }

    onLocationModalClose();
  };

  const handleDeleteLocation = (name: string) => {
    setLocations(locations.filter((l) => l.name !== name));
  };

  const handleAddLocationAlias = () => {
    if (newLocationAlias.trim() && !locationAliases.includes(newLocationAlias.trim())) {
      setLocationAliases([...locationAliases, newLocationAlias.trim()]);
      setNewLocationAlias('');
    }
  };

  const handleRemoveLocationAlias = (alias: string) => {
    setLocationAliases(locationAliases.filter((a) => a !== alias));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold">{title}</h3>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
      </CardHeader>
      <Divider />
      <CardBody className="gap-4">
        <div>
          <div className="flex justify-between items-center mb-1">
            <h4 className="font-medium">
              NPC Names ({npcs.length}/{MAX_SPELLINGS_PER_TYPE})
            </h4>
            <Button
              size="sm"
              color="primary"
              variant="flat"
              onPress={handleAddNpc}
              isDisabled={npcs.length >= MAX_SPELLINGS_PER_TYPE}>
              Add NPC Name
            </Button>
          </div>
          <p className="text-xs text-default-500 mb-2">
            Add character names to improve transcription accuracy.
          </p>
          {npcs.length === 0 ? (
            <p className="text-sm text-default-400 italic">No NPC names added yet</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {npcs.map((npc) => {
                const aliasCount = npc.aliases?.length || 0;
                const aliasText =
                  aliasCount === 0
                    ? 'No aliases'
                    : `${aliasCount} ${aliasCount === 1 ? 'alias' : 'aliases'}`;
                return (
                  <Tooltip key={npc.name} content={`${aliasText} · Click to edit`}>
                    <div
                      onClick={() => handleEditNpc(npc)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleEditNpc(npc);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer inline-block">
                      <Chip
                        onClose={() => handleDeleteNpc(npc.name)}
                        variant="flat"
                        color="primary">
                        {npc.name}
                      </Chip>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <h4 className="font-medium">
              Location Names ({locations.length}/{MAX_SPELLINGS_PER_TYPE})
            </h4>
            <Button
              size="sm"
              color="primary"
              variant="flat"
              onPress={handleAddLocation}
              isDisabled={locations.length >= MAX_SPELLINGS_PER_TYPE}>
              Add Location Name
            </Button>
          </div>
          <p className="text-xs text-default-500 mb-2">
            Add place names to improve transcription accuracy.
          </p>
          {locations.length === 0 ? (
            <p className="text-sm text-default-400 italic">No location names added yet</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {locations.map((location) => {
                const aliasCount = location.aliases?.length || 0;
                const aliasText =
                  aliasCount === 0
                    ? 'No aliases'
                    : `${aliasCount} ${aliasCount === 1 ? 'alias' : 'aliases'}`;
                return (
                  <Tooltip key={location.name} content={`${aliasText} · Click to edit`}>
                    <div
                      onClick={() => handleEditLocation(location)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleEditLocation(location);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      className="cursor-pointer inline-block">
                      <Chip
                        onClose={() => handleDeleteLocation(location.name)}
                        variant="flat"
                        color="secondary">
                        {location.name}
                      </Chip>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </div>

        {isSaving && (
          <div className="flex justify-end pt-2">
            <p className="text-sm text-default-500">Saving...</p>
          </div>
        )}
      </CardBody>

      <Modal isOpen={isNpcModalOpen} onClose={onNpcModalClose} size="lg">
        <ModalContent>
          <ModalHeader>{editingNpc ? 'Edit NPC Name' : 'Add NPC Name'}</ModalHeader>
          <ModalBody>
            <div>
              <Input
                label="Correct NPC Name"
                placeholder="e.g., Strahd von Zarovich"
                value={npcName}
                onValueChange={setNpcName}
                isRequired
              />
              <p className="text-xs text-default-500 mt-1">
                Adding your NPC's name helps us transcribe it more accurately during your sessions.
              </p>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium mb-1 block">Aliases or Misspellings</label>
              <p className="text-xs text-default-500 mb-2">
                If you've noticed misspellings in previous transcripts for this NPC, add them here
                to help us get it right next time.
              </p>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Strad, Straud, the Count"
                  value={newAlias}
                  onValueChange={setNewAlias}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAlias();
                    }
                  }}
                />
                <Button onPress={handleAddAlias} size="sm">
                  Add
                </Button>
              </div>
              {npcAliases.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {npcAliases.map((alias) => (
                    <Chip key={alias} onClose={() => handleRemoveAlias(alias)} variant="flat">
                      {alias}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onNpcModalClose}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSaveNpc} isDisabled={!npcName.trim()}>
              {editingNpc ? 'Update' : 'Add'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isLocationModalOpen} onClose={onLocationModalClose} size="lg">
        <ModalContent>
          <ModalHeader>{editingLocation ? 'Edit Location Name' : 'Add Location Name'}</ModalHeader>
          <ModalBody>
            <div>
              <Input
                label="Correct Location Name"
                placeholder="e.g., Baldur's Gate"
                value={locationName}
                onValueChange={setLocationName}
                isRequired
              />
              <p className="text-xs text-default-500 mt-1">
                Adding your location's name helps us transcribe it more accurately during your
                sessions.
              </p>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium mb-1 block">Aliases or Misspellings</label>
              <p className="text-xs text-default-500 mb-2">
                If you've noticed misspellings in previous transcripts for this location, add them
                here to help us get it right next time.
              </p>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Baldurs Gate, BG"
                  value={newLocationAlias}
                  onValueChange={setNewLocationAlias}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddLocationAlias();
                    }
                  }}
                />
                <Button onPress={handleAddLocationAlias} size="sm">
                  Add
                </Button>
              </div>
              {locationAliases.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {locationAliases.map((alias) => (
                    <Chip
                      key={alias}
                      onClose={() => handleRemoveLocationAlias(alias)}
                      variant="flat">
                      {alias}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onLocationModalClose}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleSaveLocation} isDisabled={!locationName.trim()}>
              {editingLocation ? 'Update' : 'Add'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default NpcLocationSpellingsEditor;
